import React from "react";
import { Link } from "react-router-dom";

const capabilities = [
  {
    title: "Compare alternatives",
    text: "Structure competing choices, pathways, technologies, or strategies in one transparent framework.",
  },
  {
    title: "Weight what matters",
    text: "Define criteria that reflect technical, financial, operational, environmental, or personal priorities.",
  },
  {
    title: "Test trade-offs",
    text: "See how assumptions and priorities change the ranking before committing to a direction.",
  },
  {
    title: "Clarify uncertainty",
    text: "Use scenarios to explore which decisions remain strong and which depend on specific conditions.",
  },
];

const applications = [
  {
    title: "Personal and strategic decisions",
    text: "Career moves, learning paths, business choices, investments, purchases, and other multi-factor decisions.",
  },
  {
    title: "Engineering option screening",
    text: "Early comparison of designs, configurations, technologies, constraints, and implementation pathways.",
  },
  {
    title: "Energy system decision intelligence",
    text: "A potential direction for stress-testing infrastructure, policy, and investment choices across plausible futures.",
  },
];

const engineeringSignals = [
  "Multi-criteria comparison",
  "Constraint-aware evaluation",
  "Transparent weighting logic",
  "Trade-off visibility",
  "Adaptable decision structures",
  "Reusable evaluation workflows",
];

const strategicGroups = [
  {
    title: "Policy & Ministries",
    items: ["Transition pathways", "Infrastructure direction", "Strategic robustness"],
  },
  {
    title: "Utilities & Developers",
    items: ["Grid stress testing", "Storage planning", "Energy hub configurations"],
  },
  {
    title: "Investors & Funds",
    items: ["Infrastructure risk", "Timing sensitivity", "Dependency exposure"],
  },
  {
    title: "Technology & Industry",
    items: ["Integration pathways", "System role analysis", "Scalability conditions"],
  },
];

export default function Main() {
  return (
    <main className="min-h-screen bg-gradient-to-b from-egypt-black via-egypt-blackSoft to-egypt-black text-egypt-offwhite">
      {/* Hero */}
      <section className="relative px-6 pt-16 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_#d4af37,_transparent_42%)]" />

        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center text-left">
          <div>
            <p className="inline-block mb-5 px-4 py-2 rounded-full border border-egypt-gold/40 text-egypt-gold text-sm tracking-wide uppercase">
              Decision intelligence for complex choices
            </p>

            <h1 className="text-5xl md:text-6xl font-extrabold text-egypt-gold drop-shadow-md leading-tight mb-6">
              jelliFala
            </h1>

            <h2 className="text-3xl md:text-5xl font-bold leading-tight mb-6 text-egypt-offwhite">
              See the consequences of decisions before committing to them.
            </h2>

            <p className="text-lg md:text-xl text-egypt-offwhite/85 leading-relaxed mb-8 max-w-2xl">
              jelliFala helps you turn complex choices into structured comparisons. Define alternatives, set what matters, test priorities, and understand which options hold up under changing assumptions.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/decision"
                className="inline-flex justify-center items-center bg-egypt-gold text-egypt-black px-8 py-4 rounded-xl font-bold shadow-lg hover:bg-egypt-cream transition"
              >
                Start a decision →
              </Link>
              <a
                href="#engineering"
                className="inline-flex justify-center items-center border border-egypt-gold/50 text-egypt-gold px-8 py-4 rounded-xl font-bold hover:bg-egypt-gold/10 transition"
              >
                Explore applications
              </a>
            </div>
          </div>

          <div className="flex justify-center lg:justify-end">
            <div className="relative w-full max-w-md rounded-3xl border border-egypt-gold/30 bg-egypt-black/50 p-8 shadow-goldGlow">
              <img
                src="/logo.png"
                alt="jelliFala Logo"
                className="mx-auto w-full h-auto mb-6 rounded-2xl"
              />
              <div className="grid grid-cols-3 gap-3 text-center text-sm">
                <div className="rounded-xl bg-egypt-black/70 border border-egypt-gold/20 p-3">
                  <p className="text-egypt-gold font-bold">Options</p>
                  <p className="text-egypt-offwhite/70">Define</p>
                </div>
                <div className="rounded-xl bg-egypt-black/70 border border-egypt-gold/20 p-3">
                  <p className="text-egypt-gold font-bold">Criteria</p>
                  <p className="text-egypt-offwhite/70">Weight</p>
                </div>
                <div className="rounded-xl bg-egypt-black/70 border border-egypt-gold/20 p-3">
                  <p className="text-egypt-gold font-bold">Outcome</p>
                  <p className="text-egypt-offwhite/70">Compare</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning */}
      <section className="px-6 py-16 border-y border-egypt-gold/10 bg-egypt-black/40">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-egypt-gold mb-6">
            For decisions with many moving parts
          </h2>
          <p className="text-lg text-egypt-offwhite/85 leading-relaxed max-w-3xl mx-auto">
            Some decisions are too important for intuition alone, but too contextual for a fixed template. jelliFala gives you a flexible decision workspace where you can define the logic, compare alternatives, and make trade-offs visible.
          </p>
        </div>
      </section>

      {/* Capabilities */}
      <section className="px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-egypt-gold mb-4">
              What the tool helps you do
            </h2>
            <p className="text-egypt-offwhite/75 max-w-2xl mx-auto">
              Build a decision model that is clear enough to use, adjust, explain, and reuse.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {capabilities.map((item) => (
              <article
                key={item.title}
                className="p-6 rounded-2xl bg-egypt-black/60 border border-egypt-gold/20 shadow-goldGlow"
              >
                <h3 className="text-lg font-bold text-egypt-gold mb-3">{item.title}</h3>
                <p className="text-sm text-egypt-offwhite/75 leading-relaxed">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Applications */}
      <section id="applications" className="px-6 py-20 bg-gradient-to-b from-egypt-blackSoft to-egypt-black">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-egypt-gold mb-4">
              One decision logic, multiple use cases
            </h2>
            <p className="text-egypt-offwhite/75 max-w-3xl mx-auto leading-relaxed">
              jelliFala is designed as a general decision-support environment. The same structure can support everyday choices, strategic planning, and more advanced technical evaluations where assumptions, constraints, and trade-offs matter.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {applications.map((app) => (
              <article
                key={app.title}
                className="p-7 rounded-2xl bg-egypt-black/70 border border-egypt-gold/20 shadow-goldGlow"
              >
                <h3 className="text-xl font-bold text-egypt-gold mb-3">{app.title}</h3>
                <p className="text-egypt-offwhite/80 leading-relaxed">{app.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Engineering direction */}
      <section id="engineering" className="px-6 py-20">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-sm uppercase tracking-wide text-egypt-gold/80 mb-3">
              Structured technical applications
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-egypt-gold mb-6">
              Engineering and complex system evaluation
            </h2>
            <p className="text-egypt-offwhite/85 leading-relaxed mb-5">
              Beyond personal and strategic choices, the framework can support structured evaluation in technical and engineering contexts where multiple configurations, constraints, and trade-offs must be compared transparently.
            </p>
            <p className="text-egypt-offwhite/85 leading-relaxed mb-8">
              The approach is adaptable to early-stage screening, configuration comparison, criteria-based evaluation, and multi-factor decision environments where assumptions and priorities influence outcomes.
            </p>
            <p className="text-egypt-offwhite/85 leading-relaxed mb-8">
              Rather than relying on fixed templates, the framework allows decision structures to evolve according to the complexity and context of the problem being analysed.
            </p>
            <Link
              to="/decision"
              className="inline-flex justify-center items-center bg-egypt-gold text-egypt-black px-8 py-4 rounded-xl font-bold shadow-lg hover:bg-egypt-cream transition"
            >
              Try the decision workspace →
            </Link>
          </div>

          <div className="rounded-3xl bg-egypt-black/60 border border-egypt-gold/20 p-7 shadow-goldGlow">
            <h3 className="text-2xl font-bold text-egypt-gold mb-5">
              Decision Intelligence Capabilities
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {engineeringSignals.map((signal) => (
                <div
                  key={signal}
                  className="rounded-xl bg-egypt-black/70 border border-egypt-gold/15 p-4"
                >
                  <p className="text-egypt-offwhite/85 font-medium">{signal}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-egypt-offwhite/60 leading-relaxed">
              Designed for decisions where assumptions, constraints, and consequences interact.
            </p>
          </div>
        </div>
      </section>

      {/* Strategic energy direction */}
      <section className="px-6 py-20 bg-gradient-to-b from-egypt-blackSoft to-egypt-black border-y border-egypt-gold/10">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <p className="text-sm uppercase tracking-wide text-egypt-gold/80 mb-3">
                Emerging Strategic Direction
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-egypt-gold mb-5">
                Energy System Decision Intelligence
              </h2>
              <p className="text-2xl md:text-3xl font-semibold text-egypt-offwhite leading-snug mb-6">
                Long-term energy decisions should not depend on a single future assumption.
              </p>
              <p className="text-egypt-offwhite/85 leading-relaxed mb-5">
                Energy systems are evolving through uncertain combinations of electrification, hydrogen, storage expansion, infrastructure constraints, policy shifts, and industrial transformation.
              </p>
              <p className="text-egypt-offwhite/85 leading-relaxed">
                The framework explores how strategic decisions perform across multiple plausible futures — revealing infrastructure implications, system dependencies, bottlenecks, and robust versus conditional pathways before long-term commitments are made.
              </p>
            </div>

            <div className="space-y-4">
              {strategicGroups.map((group) => (
                <article
                  key={group.title}
                  className="rounded-2xl bg-egypt-black/70 border border-egypt-gold/20 p-5 shadow-goldGlow"
                >
                  <h3 className="text-lg font-bold text-egypt-gold mb-3 uppercase tracking-wide">
                    {group.title}
                  </h3>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {group.items.map((item) => (
                      <div
                        key={item}
                        className="rounded-xl bg-egypt-black/70 border border-egypt-gold/15 px-4 py-3 text-sm text-egypt-offwhite/80"
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                </article>
              ))}

              
            </div>

          </div>
                    
          <div className="mt-10 max-w-3xl mx-auto text-center">
            <p className="text-lg md:text-xl font-medium italic text-egypt-offwhite/80 leading-relaxed">
              Some decisions reveal their risks only after the path is chosen.
            </p>
            <div className="mt-5 h-px w-24 mx-auto bg-egypt-gold/35" />
          </div>


          <div className="mt-10 rounded-2xl border border-egypt-gold/25 bg-egypt-black/70 px-6 py-5 text-center shadow-goldGlow">
            <p className="text-egypt-offwhite/85">
              <span className="font-bold text-egypt-gold">Potential pilot study directions:</span>{" "}
              Infrastructure pathways • Energy hubs • Hydrogen integration • Scenario stress-testing
            </p>
          </div>
        </div>
      </section>

      {/* Workflow + Tutorial */}
      <section className="px-6 py-20 bg-egypt-black/40 border-y border-egypt-gold/10">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-egypt-gold mb-6">
              A simple workflow for structured thinking
            </h2>

            <div className="space-y-5">
              <div className="flex gap-4 p-5 rounded-2xl bg-egypt-black/60 border border-egypt-gold/20">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-egypt-gold text-egypt-black font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-bold text-egypt-gold mb-1">Define the alternatives</h3>
                  <p className="text-egypt-offwhite/75">List the options, designs, pathways, or scenarios you want to compare.</p>
                </div>
              </div>

              <div className="flex gap-4 p-5 rounded-2xl bg-egypt-black/60 border border-egypt-gold/20">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-egypt-gold text-egypt-black font-bold">
                  2
                </div>
                <div>
                  <h3 className="font-bold text-egypt-gold mb-1">Set the criteria and weights</h3>
                  <p className="text-egypt-offwhite/75">Translate priorities into measurable factors that reflect your decision context.</p>
                </div>
              </div>

              <div className="flex gap-4 p-5 rounded-2xl bg-egypt-black/60 border border-egypt-gold/20">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-egypt-gold text-egypt-black font-bold">
                  3
                </div>
                <div>
                  <h3 className="font-bold text-egypt-gold mb-1">Compare, adjust, and learn</h3>
                  <p className="text-egypt-offwhite/75">Review rankings, test assumptions, and understand why one option performs better than another.</p>
                </div>
              </div>
            </div>
          </div>

          <aside className="rounded-3xl bg-egypt-black/60 border border-egypt-gold/20 p-6 shadow-goldGlow">
            <h3 className="text-xl font-bold text-egypt-gold mb-3">Quick tutorial</h3>
            <p className="text-sm text-egypt-offwhite/70 mb-5">
              New to the tool? Watch a short walkthrough, then open the workspace and build your own comparison.
            </p>
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                src="https://www.youtube.com/embed/FnqL7d0einE?rel=0&modestbranding=1"
                title="jelliFala Tutorial"
                className="absolute top-0 left-0 w-full h-full rounded-xl border border-egypt-gold/50 shadow-goldGlow"
                allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </aside>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="px-6 py-20 text-center">
        <div className="max-w-4xl mx-auto rounded-3xl border border-egypt-gold/30 bg-gradient-to-br from-egypt-black/80 to-egypt-blackSoft/80 p-10 shadow-goldGlow">
          <h2 className="text-3xl md:text-4xl font-bold text-egypt-gold mb-5">
            Build a decision model around what matters
          </h2>
          <p className="text-egypt-offwhite/80 leading-relaxed mb-8">
            Start with a simple comparison, then refine it into a model that reflects your assumptions, priorities, constraints, and intended outcome.
          </p>
          <Link
            to="/decision"
            className="inline-flex justify-center items-center bg-egypt-gold text-egypt-black px-8 py-4 rounded-xl font-bold shadow-lg hover:bg-egypt-cream transition"
          >
            Try jelliFala now →
          </Link>
        </div>
      </section>
    </main>
  );
}
